import React from "react";

function Pill({ children }) {
  return (
    <span className="inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200">
      {children}
    </span>
  );
}

function Stat({ value, label, sub }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="text-3xl font-bold tabular-nums">{value}</div>
      <div className="mt-1 text-sm text-gray-600">{label}</div>
      {sub ? <div className="mt-1 text-xs text-gray-400">{sub}</div> : null}
    </div>
  );
}

function Feature({ title, children }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="text-sm font-semibold">{title}</div>
      <div className="mt-2 text-sm leading-relaxed text-gray-600">
        {children}
      </div>
    </div>
  );
}

const About = () => {
  return (
    <div className="mx-auto max-w-7xl px-4 py-10">
      {/* Hero */}
      <section className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Pill>Site Safety & Compliance Platform</Pill>
          <div className="text-xs text-gray-500">
            Built for projects, audits, and real-time operations
          </div>
        </div>

        <h1 className="mt-4 text-3xl font-bold leading-tight">
          We help teams find, fix, and prevent risks — faster.
        </h1>
        <p className="mt-3 max-w-3xl text-sm text-gray-600">
          Our software unifies safety observations (UAUC), Work Permits, Toolbox
          Talks, Inductions, and Incident Reporting into one reliable, API-first
          system. Track what matters, enforce SLAs, and close the loop with
          clear ownership.
        </p>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <Pill>UAUC with SLA timers</Pill>
          <Pill>Work Permit management</Pill>
          <Pill>Induction & TBT records</Pill>
          <Pill>Incident reporting</Pill>
          <Pill>Project-wise dashboards</Pill>
        </div>
      </section>

      {/* Stats */}
      <section className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          value="10+"
          label="Integrated modules"
          sub="UAUC, WP, TBT, Induction, Incidents & more"
        />
        <Stat
          value="48h"
          label="SLA example"
          sub="Alert timeline × escalation count"
        />
        <Stat
          value="99.9%"
          label="Uptime target"
          sub="Cloud-first architecture"
        />
        <Stat
          value="API"
          label="Friendly & secure"
          sub="Role-based access, audit trails"
        />
      </section>

      {/* What we do */}
      <section className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Feature title="UAUC that actually closes the loop">
          Create observations in seconds, assign owners, and auto-calc due dates
          using your risk model (e.g.,{" "}
          <em>alertTimeline × escalationAlert.length</em>). We highlight what’s
          overdue and who’s blocking the resolution.
        </Feature>
        <Feature title="Work Permits without the paperwork">
          Issue, track, and archive permits with project-wise context. Link
          hazards and controls, attach photos, and export for audits.
        </Feature>
        <Feature title="Compliance, made visible">
          Inductions, Toolbox Talks, and Incident Reports roll into one cohesive
          record so managers see status at a glance — by project, team, and
          assignee.
        </Feature>
      </section>

      {/* How it works */}
      <section className="mt-10 rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold">How it works</h2>
        <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-gray-700">
          <li>
            <span className="font-medium">Capture:</span> Log a UAUC, permit, or
            meeting in the field (mobile-friendly).
          </li>
          <li>
            <span className="font-medium">Assign:</span> Owners and due dates
            are suggested based on your SLA rules.
          </li>
          <li>
            <span className="font-medium">Track:</span> Dashboards highlight the
            oldest open items and unresolved by person.
          </li>
          <li>
            <span className="font-medium">Close:</span> Verify actions, attach
            evidence, and auto-notify stakeholders.
          </li>
        </ol>
      </section>

      {/* Why teams choose us */}
      <section className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
        <Feature title="Built for real projects">
          Project-wise views, offline-friendly forms, and exportable records
          keep sites moving.
        </Feature>
        <Feature title="SLA & escalation engine">
          Define alert timelines and escalation chains; the system computes due
          times (e.g., 12h × 4 steps = 48h).
        </Feature>
        <Feature title="Governance & auditability">
          Immutable logs, role-based access, and consistent data models across
          modules.
        </Feature>
        <Feature title="Fast to deploy">
          API-first design, minimal training, and simple integrations into your
          stack.
        </Feature>
      </section>

      {/* Security & Compliance */}
      <section className="mt-10 rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold">Security & Compliance</h2>
        <ul className="mt-3 grid grid-cols-1 gap-2 text-sm text-gray-700 sm:grid-cols-2">
          <li>• Role-based access & least-privilege defaults</li>
          <li>• Encrypted in transit (HTTPS/TLS)</li>
          <li>• Detailed activity logs and exports</li>
          <li>• Project-level scoping & isolation</li>
        </ul>
      </section>

      {/* Roadmap (lightweight) */}
      <section className="mt-10 rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold">Roadmap Highlights</h2>
        <div className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
          <div className="rounded-xl bg-gray-50 p-4">
            <div className="font-medium">Q3</div>
            <div className="mt-1 text-gray-600">
              Bulk actions, improved exports, SLA analytics
            </div>
          </div>
          <div className="rounded-xl bg-gray-50 p-4">
            <div className="font-medium">Q4</div>
            <div className="mt-1 text-gray-600">
              Mobile app enhancements, custom risk matrices
            </div>
          </div>
          <div className="rounded-xl bg-gray-50 p-4">
            <div className="font-medium">Next</div>
            <div className="mt-1 text-gray-600">
              Deeper integrations (HRIS, SSO, webhook automations)
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mt-10 rounded-3xl border border-gray-200 bg-gradient-to-br from-indigo-50 to-white p-6 shadow-sm">
        <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
          <div>
            <h3 className="text-lg font-semibold">
              Ready to simplify safety & compliance?
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              Get a guided demo or start a pilot on one site — we’ll help you
              migrate in under a week.
            </p>
          </div>
          <div className="flex gap-2">
            <a
              href="mailto:hello@example.com?subject=Safety%20Platform%20Demo"
              className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
            >
              Request a demo
            </a>
            <a
              href="#"
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              View documentation
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
